import { hasAccessRoles } from "../utils/userHelper";
import { groupNames, type Queue, type QueueDefinition } from "./types";

const pollingInterval = 60000;

// Date 14 days ago, used for rejected queue
const today = new Date();
const date14DaysAgo = new Date(
  today.setDate(today.getDate() - 14)
).toISOString();

const allowedRoles = [
  "admin-se",
  "caseworker-se",
  "reader-se",
  "admin-all",
  "reader-all"
];

// @overWriteAccessRoles: Array of roles for specific queues
// Check if user has access to menu
function hasAccessToMenu(
  userRoles: string[],
  overwriteAccessRoles?: string[]
): boolean {
  return hasAccessRoles(userRoles, overwriteAccessRoles ?? allowedRoles);
}

// queue for active applications
function queueActiveLoanProcess(userRoles: string[]): Queue | null {
  const name = "se-active-cases-queue";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "Active",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=SE&view=queue-items&status=active&hasErrors=false",
      queueSort: "sort=updatedAt&dir=-1",
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.All_APPLICATIONS
    };
  }
  return null;
}

function queueReadyForDisbursementProcess(userRoles: string[]): Queue | null {
  const name = "se-ready-for-disbursement-cases-queue";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "Ready for disbursement",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=SE&view=queue-items&status=active&data.queues.ready-for-disbursement=true",
      queueSort: "sort=createdAt&dir=-1",
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.All_APPLICATIONS
    };
  }
  return null;
}

function queueDisbursedProcess(userRoles: string[]): Queue | null {
  const name = "se-disbursed-cases-queue";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "Disbursed",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=SE&view=queue-items&data.queues.disbursed=true",
      queueSort: "sort=updatedAt&dir=-1",
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.All_APPLICATIONS
    };
  }
  return null;
}

function queueWorkingList(userRoles: string[]): Queue | null {
  const name = "se-working-list-cases-queue";
  if (hasAccessToMenu(userRoles)) {
    // Compute timestamp dynamically to ensure each poll uses current time
    const currentTimestamp = new Date().toISOString();
    return {
      label: "Direct channel - not signed",
      name: name,
      // Direct channel applications that are approved but not signed
      // Excludes cases where no-reply timeout hasn't expired yet
      queueFilter: `?flowDefinitionId=common-process&data.country=SE&view=queue-items&status=active&data.queues.working-list=true&data.caseWork.no-reply.0.validAtWorkingListFrom=<${currentTimestamp}`,
      queueSort: "sort=createdAt&dir=-1",
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.CASEWORKER_TASKS
    };
  }
  return null;
}

function queueCardIssuedProcess(userRoles: string[]): Queue | null {
  const name = "se-card-issued-cases-queue";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "Card Issued",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=SE&view=queue-items&data.queues.disbursed=true&data.agentApiApplication.process=card-se",
      queueSort: "sort=createdAt&dir=-1",
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.All_APPLICATIONS
    };
  }
  return null;
}

function queueRefundedProcess(userRoles: string[]): Queue | null {
  const name = "se-refunded-cases-queue";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "Refunded",
      name: name,
      queueFilter:
        "?flowDefinitionId=refund&data.country=SE&view=queue-items&data.queues.disbursed=true",
      queueSort: "sort=completedAt&dir=-1",
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.All_APPLICATIONS
    };
  }
  return null;
}

function queueRejectedProcess(userRoles: string[]): Queue | null {
  const name = "se-declined-cases-queue";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "Rejected (Last 14 days)",
      name: name,
      queueFilter: `?flowDefinitionId=common-process&data.country=SE&view=queue-items&status[]=completed&status[]=archived&data.queues.disbursed=false&updatedAt=>${date14DaysAgo}`,
      queueSort: "sort=updatedAt&dir=-1",
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.All_APPLICATIONS
    };
  }
  return null;
}

// queue case worker tasks

function queueManualReviewProcess(userRoles: string[]): Queue | null {
  const name = "se-manual-casework-review";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "Income Review",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=SE&view=queue-items&status=active&data.queues.manual-payout-approval=true",
      queueSort: "sort=createdAt&dir=-1",
      pollingEnabled: false,
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.CASEWORKER_TASKS
    };
  }
  return null;
}

function queueManualAMLReviewProcess(userRoles: string[]): Queue | null {
  const name = "se-manual-casework-aml";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "AML Review",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=SE&view=queue-items&status=active&data.queues.manual-pep-review=true",
      queueSort: "sort=createdAt&dir=-1",
      pollingEnabled: false,
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.CASEWORKER_TASKS
    };
  }
  return null;
}

function queueBgCheckProcess(userRoles: string[]): Queue | null {
  const name = "se-manual-bgc-check";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "BGC manual check",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=SE&view=queue-items&status=active&data.queues.manual-bgc-handling=true",
      queueSort: "sort=createdAt&dir=-1",
      pollingEnabled: false,
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.CASEWORKER_TASKS
    };
  }
  return null;
}

function queueCrossDataCheckProcess(userRoles: string[]): Queue | null {
  const name = "se-cross-data-check";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "Cross Data Check",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=SE&view=queue-items&data.queues.cross-data-check=true",
      queueSort: "sort=createdAt&dir=-1",
      pollingEnabled: false,
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.CASEWORKER_TASKS
    };
  }
  return null;
}
function queueVerifyRefinanceProcess(userRoles: string[]): Queue | null {
  const name = "se-verify-refinance";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "Verify Refinance",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=SE&view=queue-items&status=active&data.queues.verify-refinance=true",
      queueSort: "sort=createdAt&dir=-1",
      pollingEnabled: false,
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.CASEWORKER_TASKS
    };
  }
  return null;
}

function queueRejectedLeaBankFollowUp(userRoles: string[]): Queue | null {
  const name = "se-loan-rejected-leabank-follow-up";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "Rejected Lea Bank follow up",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=SE&view=queue-items&status=active&data.queues.rejected-leabank-follow-up=true",
      queueSort: "sort=updatedAt&dir=-1",
      pollingEnabled: false,
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.CASEWORKER_TASKS
    };
  }
  return null;
}

function queueRejectedAgentFollowUp(userRoles: string[]): Queue | null {
  const name = "se-loan-rejected-agent-follow-up";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "Rejected Agent follow up",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=SE&view=queue-items&status=active&data.queues.rejected-agent-follow-up=true",
      queueSort: "sort=updatedAt&dir=-1",
      pollingEnabled: false,
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.CASEWORKER_TASKS
    };
  }
  return null;
}

function queueManualCheckFinalProcess(userRoles: string[]): Queue | null {
  const name = "se-manual-check-final";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "Disbursement Check",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=SE&view=queue-items&data.queues.manual-check-final=true&data.product=loan",
      queueSort: "sort=updatedAt&dir=-1",
      pollingEnabled: false,
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.CASEWORKER_TASKS
    };
  }
  return null;
}

function queueManualCheckFinalCardProcess(userRoles: string[]): Queue | null {
  const name = "se-card-manual-check-final";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "Card Disbursement Check",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=SE&view=queue-items&data.queues.manual-check-final=true&data.product=card",
      queueSort: "sort=updatedAt&dir=-1",
      pollingEnabled: false,
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.CASEWORKER_TASKS
    };
  }
  return null;
}

function queueAfterControlWorkingList(userRoles: string[]): Queue | null {
  const name = "se-after-control-working-list-queue";
  if (hasAccessToMenu(userRoles)) {
    const currentTimestamp = new Date().toISOString();
    return {
      label: "Disbursement check - high priority",
      name: name,
      queueFilter: `?flowDefinitionId=common-process&data.country=SE&view=queue-items&status=active&data.queues.manual-check-final-high-priority=true&data.caseWork.no-reply.0.validAtWorkingListFrom=<${currentTimestamp}&data.product=loan`,
      queueSort: "sort=createdAt&dir=-1",
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.CASEWORKER_TASKS
    };
  }
  return null;
}

function queueOnFollowUpCasesProcess(userRoles: string[]): Queue | null {
  const name = "se-on-follow-up-cases-queue";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "On Follow Up",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=SE&view=queue-items&data.queues.onFollowUp=true",
      queueSort: "sort=createdAt&dir=-1",
      pollingEnabled: true,
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.CASEWORKER_TASKS
    };
  }
  return null;
}

function queueOnHoldCasesProcess(userRoles: string[]): Queue | null {
  const name = "se-on-hold-cases-queue";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "On Hold",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=SE&view=queue-items&data.queues.onHold=true",
      queueSort: "sort=createdAt&dir=-1",
      pollingEnabled: true,
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.CASEWORKER_TASKS
    };
  }
  return null;
}

function queueManualRefundReview(userRoles: string[]): Queue | null {
  const name = "se-manual-refund-review";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "Refund Review",
      name: name,
      queueFilter:
        "?flowDefinitionId=refund&data.country=SE&view=queue-items&status=active&data.queues.manual-refund-review=true",
      queueSort: "sort=createdAt&dir=-1",
      pollingEnabled: false,
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.CASEWORKER_TASKS
    };
  }
  return null;
}

// queue for customer tasks

function queueAcceptLoanProcess(userRoles: string[]): Queue | null {
  const name = "se-awaiting-accept-cases-queue";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "Accept Loan Offer",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=SE&view=queue-items&data.queues.awaiting-accept=true",
      queueSort: "sort=updatedAt&dir=-1",
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.CUSTOMER_TASKS
    };
  }
  return null;
}

function queueStartInformationProcess(userRoles: string[]): Queue | null {
  const name = "se-start-information";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "Start Information",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=SE&view=queue-items&status=active&data.queues.start-information=true",
      queueSort: "sort=updatedAt&dir=-1",
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.CUSTOMER_TASKS
    };
  }
  return null;
}

function queueTinkConsentProcess(userRoles: string[]): Queue | null {
  const name = "se-tink-consent";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "Tink Consent",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=SE&view=queue-items&status=active&data.queues.tink-consent=true",
      queueSort: "sort=updatedAt&dir=-1",
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.CUSTOMER_TASKS
    };
  }
  return null;
}
function queueCustomerInfoProcess(userRoles: string[]): Queue | null {
  const name = "se-customer-info";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "Customer Info",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=SE&view=queue-items&status=active&data.queues.customer-info=true",
      queueSort: "sort=updatedAt&dir=-1",
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.CUSTOMER_TASKS
    };
  }
  return null;
}

function queueConfirmDebtProcess(userRoles: string[]): Queue | null {
  const name = "se-confirm-debt";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "Confirm Debt",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=SE&view=queue-items&status=active&data.queues.confirm-debt=true",
      queueSort: "sort=updatedAt&dir=-1",
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.CUSTOMER_TASKS
    };
  }
  return null;
}

function queueRefinanceProcess(userRoles: string[]): Queue | null {
  const name = "se-refinance";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "Refinance",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=SE&view=queue-items&status=active&data.queues.refinance=true",
      queueSort: "sort=updatedAt&dir=-1",
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.CUSTOMER_TASKS
    };
  }
  return null;
}

function queueInsuranceSaleProcess(userRoles: string[]): Queue | null {
  const name = "se-insurance-sale";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "Insurance Sale",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=SE&view=queue-items&status=active&data.queues.insurance-sale=true",
      queueSort: "sort=updatedAt&dir=-1",
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.CUSTOMER_TASKS
    };
  }
  return null;
}

function queueSignDocumentsProcess(userRoles: string[]): Queue | null {
  const name = "se-awaiting-signing-cases-queue";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "Sign Documents",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=SE&view=queue-items&status=active&data.queues.awaiting-signing=true",
      queueSort: "sort=updatedAt&dir=-1",
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.CUSTOMER_TASKS
    };
  }
  return null;
}

// queue for incidents
function queueFailedCasesUpdateAgentProcess(userRoles: string[]): Queue | null {
  const name = "se-failed-cases-update-agent-queue";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "Incidents (Update Agent)",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=SE&view=queue-items&status=active&hasErrors=true&data.businessStatus=!loan-disbursed&data.errors.update-agent.stamp=>0",
      queueSort: "sort=updatedAt&dir=-1",
      pollingEnabled: false,
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.INCIDENTS
    };
  }
  return null;
}

function queueFailedCasesProcess(userRoles: string[]): Queue | null {
  const name = "se-failed-cases-queue";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "Incidents",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=SE&view=queue-items&status=active&hasErrors=true&data.errors.update-agent=!",
      queueSort: "sort=updatedAt&dir=-1",
      pollingEnabled: false,
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.INCIDENTS
    };
  }
  return null;
}

function queues(userRoles: string[]): (Queue | null)[] {
  return [
    queueActiveLoanProcess(userRoles),
    queueReadyForDisbursementProcess(userRoles),
    queueDisbursedProcess(userRoles),
    queueCardIssuedProcess(userRoles),
    queueRefundedProcess(userRoles),
    queueRejectedProcess(userRoles),
    queueManualReviewProcess(userRoles),
    queueManualAMLReviewProcess(userRoles),
    queueBgCheckProcess(userRoles),
    queueCrossDataCheckProcess(userRoles),
    queueVerifyRefinanceProcess(userRoles),
    queueManualCheckFinalProcess(userRoles),
    queueManualCheckFinalCardProcess(userRoles),
    queueAfterControlWorkingList(userRoles),
    queueOnFollowUpCasesProcess(userRoles),
    queueOnHoldCasesProcess(userRoles),
    queueRejectedLeaBankFollowUp(userRoles),
    queueRejectedAgentFollowUp(userRoles),
    queueManualRefundReview(userRoles),
    queueAcceptLoanProcess(userRoles),
    queueStartInformationProcess(userRoles),
    queueTinkConsentProcess(userRoles),
    queueCustomerInfoProcess(userRoles),
    queueConfirmDebtProcess(userRoles),
    queueRefinanceProcess(userRoles),
    queueInsuranceSaleProcess(userRoles),
    queueSignDocumentsProcess(userRoles),
    queueFailedCasesUpdateAgentProcess(userRoles),
    queueFailedCasesProcess(userRoles),
    queueWorkingList(userRoles)
  ];
}

export function createQueueDefinitionsSweden(userRoles: string[]): {
  [key: string]: Queue;
} {
  const validQueues = queues(userRoles).filter(
    (queue): queue is Queue => queue !== null
  );
  const queueDefinitions = validQueues.reduce((acc: QueueDefinition, queue) => {
    acc[queue.name] = queue;
    return acc;
  }, {});
  return queueDefinitions;
}

export function createMenuDefinitionsSweden(userRoles: string[]): Queue[] {
  const validQueues = queues(userRoles).filter(
    (queue): queue is Queue => queue !== null
  );
  return validQueues;
}
